import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { AppService } from "@App";
import { MatTableDataSource } from "@angular/material/table";
import { ManagementQueue, ManagementQueueResult, SearchCasesResults, Client, Account } from "@Models";
import { DatePipe } from '@angular/common';
import { SubSink } from 'subsink';
import { interval } from 'rxjs';
import { MatSort } from "@angular/material/sort";
import { MatPaginator } from "@angular/material/paginator";
import { SharedModule } from "@Shared/shared.module";
import { DashboardSlaDueDateFilters } from "@App/my-dashboard/components/dashboard-sla-due-date-filters/dashboard-sla-due-date-filters.component";
import { QueueFilterCarouselComponent, QueueFilterItem } from "@App/my-dashboard/components/queue-filter-carousel/queue-filter-carousel.component";
import { NewBusinessWorkbenchService } from '@App/new-business-workbench/services/new-business-workbench.service';
import * as _ from 'lodash';
import { getSlaStatusFromDueDates } from '@Utils/sla-utils';
import { UserSettingsService } from "@Services/global/user-settings.service";
import { IConfigService } from 'app/config/iconfigservice';
import { BannerService, ClientService, CaseSummaryService, DashboardTasksDataService, SignalRService } from "@Services";
import { DashboardTasksComponent } from "@App/my-dashboard/components/dashboard-tasks/dashboard-tasks.component";
import { IDashboardTask } from "@CaseSummary/underwriting/models/uw-task.model";
import { HubConnection } from "@microsoft/signalr";
import DashboardTaskNotificationPayload from "@Models/dashboard-task-notification-payload";
import DashboardCategory from "@Models/dashboard-category.enum";
import { DashboardFilterBarComponent, FilterBarState, FilterBarRangeConfig, DateRangeValue, StatusOption, emptyFilterBarState } from "@App/my-dashboard/components/dashboard-filter-bar/dashboard-filter-bar.component";
import { DropdownOption } from "@Components/single-select-dropdown-list/single-select-dropdown-list.component";
import moment, { Moment } from 'moment';

interface DashboardFilterSettings {
    selectedQueueIds: string[];
    slaFilters: string[];
}

@Component({
    selector: 'dashboard-cases-component',
    templateUrl: './dashboard-cases.component.html',
    styleUrls: ['./dashboard-cases.component.scss'],
    imports: [SharedModule, DashboardSlaDueDateFilters, QueueFilterCarouselComponent, DashboardTasksComponent, DashboardFilterBarComponent]
})
export class DashboardCasesComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() clientId: string;
    private _sort: MatSort;
    private _paginator: MatPaginator;

    @ViewChild(MatSort)
    set sort(sort: MatSort) {
        this._sort = sort;
        this.assignTableFeatures();
    }
    get sort(): MatSort {
        return this._sort;
    }

    @ViewChild(MatPaginator)
    set paginator(paginator: MatPaginator) {
        this._paginator = paginator;
        this.assignTableFeatures();
    }
    get paginator(): MatPaginator {
        return this._paginator;
    }

    @ViewChild('casesSlaFilters') casesSlaFilters?: DashboardSlaDueDateFilters;

    caseDataSource = new MatTableDataSource<ManagementQueueResult>();
    myCasesQueueId = "05A68876-15B9-4318-874F-7BD979E28C96";
    subs = new SubSink();
    cases: ManagementQueueResult[] = [];
    timeZone: string;
    uniqueQueueIds: string[] = [];
    selectedQueueIds: string[] = [];
    queues: ManagementQueue[] = [];
    columnsToDisplay = [];
    dynamicColumns = [];
    notesColumns = [];
    filters: QueueFilterItem[] = [];
    slaFilters: any[] = [];
    slaDueDateCounts: any = {
        pastDue: 0,
        dueToday: 0,
        dueTomorrow: 0
    };
    isSLAFilterApplied: boolean = false;
    activeFilterCount: number = 0;
    
    filterValue: any;

    accountOptions: DropdownOption[] = [];
    statusOptions: StatusOption[] = [];
    private readonly accountsById = new Map<string, Account>();
    filterState: FilterBarState = emptyFilterBarState();
    readonly dateRanges: FilterBarRangeConfig[] = [
        { key: 'slaDue', label: 'SLA Due Date' },
        { key: 'created', label: 'Created Date' }
    ];

    isSideNavHidden: boolean = false;
    atLeastOneQueueHasSLA = false;
    tasks: IDashboardTask[] = [];
    private signalrConnection: HubConnection;

    constructor(
        public appService: AppService,
        public datePipe: DatePipe,
        public newBusinessWorkbenchService: NewBusinessWorkbenchService,
        public caseSummaryService: CaseSummaryService,
        private readonly userSettingsService: UserSettingsService,
        private readonly bannerService: BannerService,
        private readonly dashboardTasksService: DashboardTasksDataService,
        private readonly signalrService: SignalRService,
        private readonly clientService: ClientService,
        private readonly configService: IConfigService
    ) { }

    ngOnInit(): void {
        this.setupSignalR();
        this.getQueues();
        this.timeZone = this.appService.getTimeZone();
        this.loadSavedFilters();
        this.loadAccounts();
        this.getTasks();
        this.startSlaRecomputeTimer();
    }

    ngAfterViewInit() {
        this.assignTableFeatures();

        const sidenav = document.getElementById('sidenav');
        if (sidenav) {
            const observer = new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                    if (mutation.attributeName === 'class') {
                        this.isSideNavHidden = sidenav.classList.contains('hidenav');
                    }
                });
            });
            observer.observe(sidenav, {
                attributes: true,
                attributeFilter: ['class']
            });
        }
    }

    getQueues() {
        this.newBusinessWorkbenchService.getAllWorkbenchQueues(this.clientId).subscribe(
            results => {
                this.queues = results.filter(x => !x.isHidden);
                this.setColumnsAndGetCases();
                this.atLeastOneQueueHasSLA = this.queues.some(q => q.isServiceLevelAgreementEnabled && q.serviceLevelAgreement.hours);

                if (this.atLeastOneQueueHasSLA) {
                    this.bannerService.clearDismissal('no-sla-configured');
                } else {
                    const canConfigure = this.appService.checkACL('Case Manager: Configure', 'W');
                    this.bannerService.showBanner({
                        title: 'Unlock SLA tracking for your queues.',
                        message: 'Set response and resolution targets so your team can see what\'s breached, what\'s at risk, and where to focus first.',
                        dismissKey: 'no-sla-configured',
                        actions: canConfigure
                            ? [{ label: 'CONFIGURE SLAS', route: '/casemanager/edit', variant: 'primary' }]
                            : []
                    });
                }
            }
        );
    }

    ngOnDestroy(): void {
        this.bannerService.clearBanner();
        this.subs.unsubscribe();
    }

    private startSlaRecomputeTimer(): void {
        const slaRecomputeInterval = this.configService.getConfiguration().slaRecomputeInterval;
        const intervalValue = Number.parseInt(slaRecomputeInterval);
        this.subs.sink = interval(intervalValue)
            .subscribe(() => this.recomputeSlaCounts());
    }

    private recomputeSlaCounts(): void {
        if (!this.cases?.length) {
            return;
        }

        this.calculateSlaCounts(this.cases);
        this.applyAllFilters();
    }

    getTasks(hideWaiting: boolean = false): void {
        this.dashboardTasksService.getMyTasks(this.appService.getUserId(), hideWaiting).subscribe(tasks => {
            this.tasks = tasks;
        });
    }

    setColumnsAndGetCases() {
        const queue = this.queues.find(q => q.id.toUpperCase() === this.myCasesQueueId);
        if (!queue) return;
        const sortedColumns = _.orderBy(queue.managementQueueColumnMapKeys, ['order']); // sort configured columns
        const uniqueColumns = _.uniqBy(sortedColumns.filter(x => x.columnDefinition != 'selection'), 'columnDefinition'); // remove duplicates if any
        this.columnsToDisplay = uniqueColumns.map(c => c.columnDefinition); // set columns to display based on the above

        this.dynamicColumns = uniqueColumns.filter(x => x.columnDefinition != 'note' && x.columnDefinition != 'actions');
        this.notesColumns = uniqueColumns.filter(x => x.columnDefinition == 'note');
        this.getMyCases();
    }

    getMyCases(hideWaiting: boolean = false): void {
        this.appService.getData(`CaseManager/Search/${this.myCasesQueueId}`, null, hideWaiting).subscribe(
            results => {
                this.cases = (results.data || []).map(c => ({
                    ...c,
                    caseCreatedDateFormatted: this.datePipe.transform(c.caseCreatedDate, 'MM/dd/yyyy', this.timeZone),
                    usersInCase: this.getPresenceUsers(c),
                    ...c.mapkeys.reduce((dict, next) => {
                        dict[next.name] = next.value;
                        return dict;
                    }, {})
                }));

                this.isSLAFilterApplied = true;

                this.calculateSlaCounts(this.cases);
                this.extractUniqueCaseQueueIds(this.cases);
                this.pruneStaleQueueFilters();
                this.buildAccountOptions(this.cases);
                this.pruneStaleAccountFilters();
                this.buildStatusOptions(this.cases);
                this.applyAllFilters();
                this.assignTableFeatures();
            });
    }

    assignTableFeatures(): void {
        if (!this.caseDataSource || !this._sort || !this._paginator) {
            return;
        }
        this.caseDataSource.sort = this._sort;
        this.caseDataSource.paginator = this._paginator;
        this.caseDataSource.sortingDataAccessor = (item, property) => {
            const value = item[property];
            if (this.isValidDate(value)) {
                return new Date(value).getTime();
            } else {
                return value;
            }
        };
    }

    getPresenceUsers(data: SearchCasesResults) {
        let result = null;
        if (data.presenceUsers && data.presenceUsers.length > 0) {
            result = Object.keys(data.presenceUsers).map(function (k) {
                return data.presenceUsers[k].name;
            }).join(', ');
        }
        return result;
    }

    getQueueName(queueId: string): string | undefined {
        const queue = this.queues.find(q => q.id === queueId);
        return queue?.name;
    }

    generateFilters(filteredCases: ManagementQueueResult[]) {
        const filters = [{
            id: this.myCasesQueueId,
            name: 'All Cases',
            count: filteredCases.length,
            checked: this.isAllCasesChecked()
        }];
    

        const queueCounts = new Map<string, number>();

        for (const id of this.uniqueQueueIds) {
            queueCounts.set(id, 0);
        }

        for (const c of filteredCases) {
            for (const { item1 } of c.caseQueueSlaDueDates ?? []) {
                if (queueCounts.has(item1)) {
                    queueCounts.set(item1, queueCounts.get(item1) + 1);
                }
            }
        }

        for (const id of this.uniqueQueueIds) {
            const queueName = this.getQueueName(id);
            if (!queueName) {
                continue;
            }
            const count = queueCounts.get(id) || 0;

            filters.push({
                id,
                name: queueName,
                count,
                checked: this.selectedQueueIds.includes(id)
            });
        }
        filters.sort((a, b) => {
            // give priority to "My Cases" queue
            if (a.id === this.myCasesQueueId) return -1;
            if (b.id === this.myCasesQueueId) return 1; 

            const queueA = this.queues.find(q => q.id === a.id);
            const queueB = this.queues.find(q => q.id === b.id);
            return queueA?.order - queueB?.order;
        });
        this.filters = filters;
    }

    isAllCasesChecked(): boolean {
        return this.selectedQueueIds.length === 0;
    }

    getSelectedFiltersCount(): number {
        return this.selectedQueueIds.length;
    }

    updateFilteredCases() {
        if (this.filterValue) {
            this.caseDataSource.filter = this.filterValue.trim().toLowerCase();
        } else {
            this.caseDataSource.filter = null;
        }
    }

    handleAllCasesEmitted(checked: boolean) {
        if (checked) {
            this.selectedQueueIds = [];
            this.applyAllFilters();
        }
    }

    onQueueFilterEmitted(value: string) {
        if (value === this.myCasesQueueId) {
            this.handleAllCasesEmitted(true);
        } else {
            this.handleValueEmitted(value);
        }
    }

    handleValueEmitted(queueId: string) {
        this.toggleQueueSelection(queueId);
    }

    toggleQueueSelection(queueId: string) {
        if (queueId === this.myCasesQueueId) {
            this.selectedQueueIds = [];
        } else {
            const idx = this.selectedQueueIds.indexOf(queueId);
            if (idx === -1) {
                this.selectedQueueIds = [...this.selectedQueueIds, queueId];
            } else {
                this.selectedQueueIds = this.selectedQueueIds.filter(id => id !== queueId);
            }
        }
        this.applyAllFilters();
        this.saveFilters();
    }

    updateFiltersCheckedState() {
        this.filters.forEach(f => {
            f.checked = f.id === this.myCasesQueueId
                ? this.selectedQueueIds.length === 0
                : this.selectedQueueIds.includes(f.id);
        });
    }

    extractUniqueCaseQueueIds(cases: ManagementQueueResult[]): void {
        const ids = new Set<string>();

        for (const c of cases) {
            for (const { item1 } of c.caseQueueSlaDueDates ?? []) {
                const matchingQueue = this.queues.find(q => q.id === item1);
                if (!matchingQueue) {
                    continue;
                }
                ids.add(item1);
            }
        }

        this.uniqueQueueIds = Array.from(ids);
    }

    private pruneStaleQueueFilters(): void {
        const validIds = new Set(this.uniqueQueueIds);
        const pruned = this.selectedQueueIds.filter(id => validIds.has(id));
        if (pruned.length !== this.selectedQueueIds.length) {
            this.selectedQueueIds = pruned;
            this.saveFilters();
        }
    }

    private pruneStaleAccountFilters(): void {
        const validIds = new Set(this.accountOptions.map(o => o.value));
        const pruned = this.filterState.accountIds.filter(id => validIds.has(id));
        if (pruned.length !== this.filterState.accountIds.length) {
            this.filterState = { ...this.filterState, accountIds: pruned };
        }
    }

    viewCaseDetails(record, newTab = true) {
        this.caseSummaryService.routeToCase(record, newTab);
    }
    
    handleslaFiltersEmitted(value: string) {
        this.slaFilters = value.split(',');
        if(this.slaFilters.length === 1 && this.slaFilters[0] === '') {
            this.slaFilters = [];
        }
        this.isSLAFilterApplied = true;
        this.applyAllFilters();
        this.saveFilters();
    }

    private loadSavedFilters(): void {
        this.userSettingsService.getSetting<DashboardFilterSettings>(UserSettingsService.DASHBOARD_CASES_FILTERS_KEY).subscribe(saved => {
            if (!saved) return;
            let restored = false;
            if (saved.selectedQueueIds?.length) {
                this.selectedQueueIds = saved.selectedQueueIds;
                restored = true;
            }
            if (saved.slaFilters?.length) {
                this.slaFilters = saved.slaFilters;
                this.isSLAFilterApplied = true;
                restored = true;
            }
            if (restored) {
                this.applyAllFilters();
            }
        });
    }

    private saveFilters(): void {
        const settings: DashboardFilterSettings = {
            selectedQueueIds: this.selectedQueueIds,
            slaFilters: this.slaFilters
        };
        this.userSettingsService.saveSetting(UserSettingsService.DASHBOARD_CASES_FILTERS_KEY, settings).subscribe();
    }

    getSlaStatus(c: ManagementQueueResult): string[] {
        if(!c.caseQueueSlaDueDates || c.caseQueueSlaDueDates.length === 0) {
            return [];
        }
        const results = getSlaStatusFromDueDates(c.caseQueueSlaDueDates);
        c.slaStatus = results;
        return results;
    }

    calculateSlaCounts(cases: ManagementQueueResult[]): void {
        const counts = {
            pastDue: 0,
            dueToday: 0,
            dueTomorrow: 0
        };

        for (const c of cases) {
            const slaStatus = this.getSlaStatus(c);
            for (const status of slaStatus) {
                if(counts.hasOwnProperty(status)) {
                    counts[status]++;
                }
            }
        }

        this.slaDueDateCounts = { ...counts };
    }

    applyAllFilters() {
        let filtered = this.cases;

        if(this.slaFilters && this.slaFilters.length > 0) {
            filtered = filtered.filter(c => {
                return c.slaStatus && this.slaFilters.some(filter => c.slaStatus.includes(filter));
            });
        }

        if(this.isSLAFilterApplied) {
            this.generateFilters(filtered);
            this.isSLAFilterApplied = false;
        }

        if(this.selectedQueueIds.length > 0) {
            filtered = filtered.filter(c => (c.caseQueueSlaDueDates || []).some(({ item1 }) => this.selectedQueueIds.includes(item1)));
        }

        const f = this.filterState;

        if (f.accountIds.length > 0) {
            filtered = filtered.filter(c => f.accountIds.includes((c as any).accountId));
        }

        if (f.statuses.length > 0) {
            filtered = filtered.filter(c => f.statuses.includes(c.status));
        }

        const slaDue = this.getRange('slaDue');
        if (slaDue.start || slaDue.end) {
            filtered = filtered.filter(c => (c.caseQueueSlaDueDates || []).some(({ item2 }) => this.isWithinRange(item2, slaDue.start, slaDue.end)));
        }

        const created = this.getRange('created');
        if (created.start || created.end) {
            filtered = filtered.filter(c => this.isWithinRange((c as any).caseCreatedDate ?? (c as any).caseCreated, created.start, created.end));
        }

        this.caseDataSource.data = filtered;

        const accountCount = f.accountIds.length > 0 ? 1 : 0;
        const statusCount = f.statuses.length > 0 ? 1 : 0;
        const slaDueCount = (slaDue.start || slaDue.end) ? 1 : 0;
        const createdCount = (created.start || created.end) ? 1 : 0;
        const searchCount = f.searchTerm.trim() ? 1 : 0;

        this.activeFilterCount = this.getSelectedFiltersCount()
            + (this.slaFilters ? this.slaFilters.length : 0)
            + accountCount + statusCount + slaDueCount + createdCount + searchCount;

        this.calculateSlaCounts(filtered);
        this.updateFiltersCheckedState();
    }

    onFiltersChange(state: FilterBarState): void {
        this.filterState = state;
        this.filterValue = state.searchTerm;
        this.updateFilteredCases();
        this.applyAllFilters();
    }

    onFiltersCleared(): void {
        this.filterState = emptyFilterBarState();
        this.filterValue = null;
        this.updateFilteredCases();
        this.selectedQueueIds = [];
        this.slaFilters = [];
        this.isSLAFilterApplied = true;
        this.casesSlaFilters?.clearSelections();
        this.applyAllFilters();
        this.saveFilters();
    }

    loadAccounts(): void {
        this.subs.sink = this.clientService.getClientsAndAccounts(false, true)
            .subscribe((clients: Client[]) => {
                this.indexAccounts(clients);
                this.buildAccountOptions(this.cases);
            });
    }

    buildAccountOptions(cases: ManagementQueueResult[]): void {
        const accountMap = new Map<string, string>();
        (cases || []).forEach((c: any) => {
            const accountId = c.accountId;
            if (accountId && !accountMap.has(accountId)) {
                accountMap.set(accountId, this.resolveAccountLabel(c));
            }
        });
        this.accountOptions = Array.from(accountMap.entries())
            .map(([value, displayName]) => ({ value, displayName }))
            .sort((a, b) => a.displayName.localeCompare(b.displayName));
    }

    buildStatusOptions(cases: ManagementQueueResult[]): void {
        const statusMap = new Map<string, string>();
        (cases || []).forEach(c => {
            if (c.status && !statusMap.has(c.status)) {
                statusMap.set(c.status, c.statusName ?? c.status);
            }
        });
        this.statusOptions = Array.from(statusMap.entries())
            .map(([value, displayName]) => ({ value, displayName }))
            .sort((a, b) => a.displayName.localeCompare(b.displayName));
    }

    private indexAccounts(clients: Client[]): void {
        this.accountsById.clear();
        (clients || []).forEach(client => {
            (client.accounts || []).forEach(account => {
                this.indexAccount(account, client.code);
                (account.versions || []).forEach(version => this.indexAccount(version, client.code));
            });
        });
    }

    private indexAccount(account: Account, clientCode: string): void {
        if (account.id && !this.accountsById.has(account.id)) {
            this.accountsById.set(account.id, { ...account, clientCode: account.clientCode || clientCode });
        }
    }

    private resolveAccountLabel(c: any): string {
        const match = this.accountsById.get(c.accountId);
        const name = (match?.name ?? c.accountName)?.trim();
        const accountCode = (match?.code ?? c.accountCode)?.trim();
        if (name && accountCode) return `${name} (${accountCode})`;
        return name || accountCode || '';
    }

    private getRange(key: string): DateRangeValue {
        return this.filterState.ranges[key] ?? { start: null, end: null };
    }

    private isWithinRange(date: string | undefined, start: Moment | null, end: Moment | null): boolean {
        if (!date) return false;
        const parsed = moment(date);
        if (!parsed.isValid()) return false;
        const dateKey = parsed.format('YYYY-MM-DD');
        if (start && dateKey < start.format('YYYY-MM-DD')) return false;
        if (end && dateKey > end.format('YYYY-MM-DD')) return false;
        return true;
    }

    isValidDate(value: string): boolean {
        if (!value) return false;

        const date = Date.parse(value);
        return !Number.isNaN(date);
    }

    //#region SignalR
    setupSignalR() {
        // Create HubConnection
        this.signalrConnection = this.signalrService.getUserSignalRConnection(this.appService.getUserId());
        this.signalrConnection.serverTimeoutInMilliseconds = 1000 * 60 * 2; // 2 minutes

        // Setup Listeners

        this.signalrConnection.on('connected', (event) => {
            console.log('SignalR Connected for Dashboard Tasks : ' + event);
        });

        this.signalrConnection.on('dashboardTaskNotification', (payload: DashboardTaskNotificationPayload) => {
            const currentUserId = this.appService.getUserId();
            if (!payload?.userId || payload.userId !== currentUserId) return;
            if (!payload.category || payload.category === DashboardCategory.Tasks) {
                this.getTasks(true);
            }
            if (!payload.category || payload.category === DashboardCategory.Cases) {
                this.getMyCases(true);
            }
        });

        this.signalrConnection.onclose(this.signalrOnClose.bind(this));

        // Start SignalR
        this.signalrConnection.start().then(this.signalrStarted.bind(this)).catch(this.signalrStartError);
    }

    signalrStarted() {
        console.log('Dashboard Tasks Component | SignalR connection started');
    }

    signalrStartError(error: any) {
        console.error(error.message);
    }

    signalrOnClose(error: any) {
        console.log('Dashboard Tasks Component | SignalR Disconnected');
    }
    //#endregion

}